/**
 * Tema claro u oscuro, elegible por la persona que usa el sistema.
 *
 * POR QUE HACIA FALTA
 * ===================
 * El tema oscuro ya estaba escrito: `globals.css` define la paleta completa
 * bajo `[data-theme="dark"]`, con sus propios valores de contraste y no una
 * inversion automatica. Pero **no habia forma de encenderlo**: ni proveedor,
 * ni interruptor, ni preferencia guardada. Trabajo de diseno hecho y muerto.
 *
 * Y no es una preferencia estetica. Las salas de lectura de radiologia se
 * mantienen a media luz para poder distinguir los grises de una imagen: una
 * pantalla blanca al lado del visor cansa la vista y, peor, contrae la pupila
 * y le quita sensibilidad al ojo para lo que de verdad importa.
 *
 * TRES ESTADOS, NO DOS
 * ====================
 * `sistema` es el valor por defecto y sigue la configuracion del equipo. Solo
 * cuando la persona elige explicitamente se guarda su decision. Asi quien no
 * toca nada obtiene lo que ya prefiere, y quien elige manda sobre el sistema.
 *
 * @module application/tema
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export type Tema = 'claro' | 'oscuro' | 'sistema'

const CLAVE = 'jh_lumina_tema'

interface ContextoDeTema {
  /** Lo que la persona eligio. */
  readonly tema: Tema
  /** Lo que se esta viendo ahora mismo, ya resuelto. */
  readonly oscuroActivo: boolean
  readonly cambiarTema: (tema: Tema) => void
}

const Contexto = createContext<ContextoDeTema | null>(null)

const leerGuardado = (): Tema => {
  if (typeof window === 'undefined') return 'sistema'
  const valor = window.localStorage.getItem(CLAVE)
  return valor === 'claro' || valor === 'oscuro' ? valor : 'sistema'
}

const sistemaPrefiereOscuro = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches

export function ProveedorDeTema({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(leerGuardado)
  const [oscuroActivo, setOscuroActivo] = useState(
    () => leerGuardado() === 'oscuro' || (leerGuardado() === 'sistema' && sistemaPrefiereOscuro())
  )

  useEffect(() => {
    const aplicar = () => {
      const oscuro = tema === 'oscuro' || (tema === 'sistema' && sistemaPrefiereOscuro())
      setOscuroActivo(oscuro)

      // Se marca la raiz del documento: de ahi cuelgan tanto las variables
      // CSS del tema como la variante `dark:` de Tailwind.
      document.documentElement.setAttribute('data-theme', oscuro ? 'dark' : 'light')
      document.documentElement.style.colorScheme = oscuro ? 'dark' : 'light'
    }

    aplicar()

    // Si la persona dejo "sistema", seguir al sistema cuando cambie: los
    // equipos suelen alternar solos entre el dia y la noche.
    if (tema !== 'sistema') return
    const consulta = window.matchMedia('(prefers-color-scheme: dark)')
    consulta.addEventListener('change', aplicar)
    return () => consulta.removeEventListener('change', aplicar)
  }, [tema])

  const cambiarTema = (nuevo: Tema) => {
    setTema(nuevo)
    if (nuevo === 'sistema') window.localStorage.removeItem(CLAVE)
    else window.localStorage.setItem(CLAVE, nuevo)
  }

  return (
    <Contexto.Provider value={{ tema, oscuroActivo, cambiarTema }}>
      {children}
    </Contexto.Provider>
  )
}

export function useTema(): ContextoDeTema {
  const contexto = useContext(Contexto)
  if (!contexto) throw new Error('useTema debe usarse dentro de ProveedorDeTema')
  return contexto
}
